// apps/web/src/pages/PrivacyPolicy.tsx
import React, { useEffect } from 'react';

const PrivacyPolicy: React.FC = () => {
  useEffect(() => {
    document.title = 'Gizlilik Politikası - SavNot';
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-gray-700">
          {/* Header */}
          <div className="mb-8 text-center">
            <h1 className="text-4xl font-bold text-white mb-2">
              Gizlilik Politikası
            </h1>
            <p className="text-gray-400">Son Güncelleme: 11 Aralık 2025</p>
          </div>

          <div className="prose prose-invert max-w-none">
            {/* Introduction */}
            <section className="mb-8">
              <p className="text-gray-300 leading-relaxed">
                SavNot olarak gizliliğinize önem veriyoruz. Bu politika, kişisel verilerinizin nasıl toplandığını, 
                kullanıldığını ve korunduğunu açıklar. KVKK (Kişisel Verilerin Korunması Kanunu) ve GDPR uyumlu 
                olarak hareket ediyoruz.
              </p>
            </section>

            {/* 1. Toplanan Bilgiler */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                <span className="text-emerald-400 mr-2">1.</span>
                Toplanan Bilgiler
              </h2>
              <div className="bg-gray-900/50 rounded-lg p-6 border border-gray-700">
                <h3 className="text-lg font-semibold text-emerald-400 mb-3">Hesap Bilgileri:</h3>
                <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                  <li>E-posta adresi (zorunlu)</li>
                  <li>Kullanıcı adı (zorunlu)</li>
                  <li>Doğum tarihi (yaş doğrulaması için)</li>
                  <li>Cinsiyet (isteğe bağlı)</li>
                  <li>Profil fotoğrafı (isteğe bağlı)</li>
                </ul>

                <h3 className="text-lg font-semibold text-emerald-400 mb-3">İçerik Bilgileri:</h3>
                <ul className="list-disc list-inside text-gray-300 space-y-2 mb-4">
                  <li>Paylaştığınız gönderiler ve yorumlar</li>
                  <li>Yüklediğiniz fotoğraflar</li>
                  <li>Oylar ve etkileşimler</li>
                  <li>Mesajlar ve bildirimler</li>
                </ul>

                <h3 className="text-lg font-semibold text-emerald-400 mb-3">Teknik Bilgiler:</h3>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li>IP adresi</li>
                  <li>Cihaz bilgileri (model, işletim sistemi)</li>
                  <li>Tarayıcı türü ve sürümü</li>
                  <li>Kullanım istatistikleri</li>
                </ul>
              </div>
            </section>

            {/* 2. Bilgilerin Kullanımı */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                <span className="text-emerald-400 mr-2">2.</span>
                Bilgilerin Kullanımı
              </h2>
              <div className="bg-gray-900/50 rounded-lg p-6 border border-gray-700">
                <p className="text-gray-300 mb-3">Topladığımız bilgileri şu amaçlarla kullanırız:</p>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li><strong>Hizmet Sunumu:</strong> Platformu çalıştırmak ve geliştirmek</li>
                  <li><strong>İletişim:</strong> Önemli güncellemeler ve bildirimler göndermek</li>
                  <li><strong>Güvenlik:</strong> Dolandırıcılığı önlemek ve güvenliği sağlamak</li>
                  <li><strong>Kişiselleştirme:</strong> İçerik önerilerini iyileştirmek</li>
                  <li><strong>Analiz:</strong> Kullanım istatistikleri ve performans analizi</li>
                  <li><strong>Moderasyon:</strong> Topluluk kurallarını uygulamak</li>
                </ul>
              </div>
            </section>

            {/* 3. Veri Paylaşımı */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                <span className="text-emerald-400 mr-2">3.</span>
                Veri Paylaşımı ve Aktarımı
              </h2>
              <div className="bg-gray-900/50 rounded-lg p-6 border border-gray-700">
                <p className="text-gray-300 mb-3">Verilerinizi üçüncü taraflarla <strong>satmıyoruz</strong>. 
                Ancak şu durumlarda paylaşabiliriz:</p>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li><strong>Hizmet Sağlayıcılar:</strong> Bulut hosting (Google Cloud Platform), analitik araçları</li>
                  <li><strong>Yasal Zorunluluklar:</strong> Mahkeme kararı veya yasal süreçler</li>
                  <li><strong>İş Ortakları:</strong> Ödeme işlemcileri (Premium özellikler için)</li>
                  <li><strong>Kullanıcı İzni:</strong> Açık onayınızla paylaşım</li>
                </ul>
              </div>
            </section>

            {/* 4. Veri Güvenliği */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                <span className="text-emerald-400 mr-2">4.</span>
                Veri Güvenliği
              </h2>
              <div className="bg-gray-900/50 rounded-lg p-6 border border-gray-700">
                <p className="text-gray-300 mb-3">Verilerinizi korumak için:</p>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li>🔒 <strong>SSL/TLS şifreleme:</strong> Tüm veri iletimi şifrelenir</li>
                  <li>🔐 <strong>Güvenli şifre saklama:</strong> Bcrypt hash algoritması</li>
                  <li>🛡️ <strong>Erişim kontrolü:</strong> Rol tabanlı yetkilendirme</li>
                  <li>📊 <strong>Düzenli denetim:</strong> Güvenlik açıklarını tarama</li>
                  <li>💾 <strong>Yedekleme:</strong> Düzenli veri yedeği</li>
                </ul>
              </div>
            </section>

            {/* 5. Çerezler (Cookies) */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                <span className="text-emerald-400 mr-2">5.</span>
                Çerezler ve Tracking
              </h2>
              <div className="bg-gray-900/50 rounded-lg p-6 border border-gray-700">
                <p className="text-gray-300 mb-3">Kullandığımız çerezler:</p>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li><strong>Zorunlu Çerezler:</strong> Oturum yönetimi, güvenlik</li>
                  <li><strong>Performans Çerezleri:</strong> Sayfa yüklenme hızı, hata tespiti</li>
                  <li><strong>Fonksiyonel Çerezler:</strong> Tercihlerinizi hatırlama (tema, dil)</li>
                  <li><strong>Analitik Çerezler:</strong> Kullanım istatistikleri (anonim)</li>
                </ul>
                <p className="text-gray-400 mt-3 text-sm">
                  Tarayıcı ayarlarınızdan çerezleri silebilir veya engelleyebilirsiniz.
                </p>
              </div>
            </section>

            {/* 6. Kullanıcı Hakları */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                <span className="text-emerald-400 mr-2">6.</span>
                Haklarınız (KVKK/GDPR)
              </h2>
              <div className="bg-gray-900/50 rounded-lg p-6 border border-gray-700">
                <p className="text-gray-300 mb-3">Kişisel verileriniz üzerinde şu haklara sahipsiniz:</p>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li>✅ <strong>Erişim Hakkı:</strong> Hangi verilerinizin işlendiğini öğrenme</li>
                  <li>✏️ <strong>Düzeltme Hakkı:</strong> Yanlış bilgileri düzeltme</li>
                  <li>🗑️ <strong>Silme Hakkı:</strong> Verilerinizi silme ("Unutulma hakkı")</li>
                  <li>📦 <strong>Taşınabilirlik:</strong> Verilerinizi başka platforma taşıma</li>
                  <li>🚫 <strong>İtiraz Hakkı:</strong> Veri işlemeye itiraz etme</li>
                  <li>⏸️ <strong>Kısıtlama Hakkı:</strong> İşlemeyi durdurma talebi</li>
                </ul>
                <div className="mt-4 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  <p className="text-emerald-400 text-sm">
                    <strong>Haklarınızı kullanmak için:</strong> privacy@savnot.com adresine e-posta gönderin 
                    veya uygulama içinden "Ayarlar → Gizlilik → Veri Talebi" bölümünü kullanın.
                  </p>
                </div>
              </div>
            </section>

            {/* 7. Veri Saklama Süresi */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                <span className="text-emerald-400 mr-2">7.</span>
                Veri Saklama Süresi
              </h2>
              <div className="bg-gray-900/50 rounded-lg p-6 border border-gray-700">
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li><strong>Aktif Hesaplar:</strong> Hesap kapanana kadar saklanır</li>
                  <li><strong>Silinen Hesaplar:</strong> 30 gün içinde kalıcı olarak silinir</li>
                  <li><strong>İçerik Verileri:</strong> Kullanıcı silene kadar saklanır</li>
                  <li><strong>Log Kayıtları:</strong> Güvenlik amaçlı 90 gün saklanır</li>
                  <li><strong>Yasal Yükümlülükler:</strong> Kanuni süre boyunca saklanır</li>
                </ul>
              </div>
            </section>

            {/* 8. Çocukların Gizliliği */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                <span className="text-emerald-400 mr-2">8.</span>
                Çocukların Gizliliği
              </h2>
              <div className="bg-gray-900/50 rounded-lg p-6 border border-gray-700">
                <p className="text-gray-300">
                  SavNot, <strong>13 yaş ve üzeri</strong> kullanıcılar içindir. 13 yaşından küçük kullanıcılardan 
                  bilerek veri toplamıyoruz. Eğer 13 yaşından küçük bir kullanıcı tespit edersek, hesabı derhal 
                  kapatır ve verilerini sileriz.
                </p>
              </div>
            </section>

            {/* 9. Uluslararası Veri Transferi */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                <span className="text-emerald-400 mr-2">9.</span>
                Uluslararası Veri Transferi
              </h2>
              <div className="bg-gray-900/50 rounded-lg p-6 border border-gray-700">
                <p className="text-gray-300 mb-3">
                  Verileriniz Google Cloud Platform üzerinde saklanır ve işlenir. Sunucularımız:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li>🌍 <strong>Konum:</strong> Avrupa Birliği (GDPR uyumlu bölge)</li>
                  <li>🔒 <strong>Sertifikalar:</strong> ISO 27001, SOC 2</li>
                  <li>📜 <strong>Veri Transferi:</strong> Standart sözleşme hükümleri (SCC)</li>
                </ul>
              </div>
            </section>

            {/* 10. Değişiklikler */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                <span className="text-emerald-400 mr-2">10.</span>
                Politika Değişiklikleri
              </h2>
              <div className="bg-gray-900/50 rounded-lg p-6 border border-gray-700">
                <p className="text-gray-300">
                  Bu politikayı zaman zaman güncelleyebiliriz. Önemli değişiklikler için sizi e-posta ile 
                  bilgilendiririz. Son güncelleme tarihi sayfanın üstünde belirtilir. Devam eden kullanım, 
                  güncel politikayı kabul ettiğiniz anlamına gelir.
                </p>
              </div>
            </section>

            {/* Contact */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                <span className="text-emerald-400 mr-2">📧</span>
                İletişim
              </h2>
              <div className="bg-gradient-to-r from-emerald-500/10 to-blue-500/10 rounded-lg p-6 border border-emerald-500/30">
                <p className="text-gray-300 mb-4">
                  Gizlilik politikası hakkında sorularınız veya talepleriniz için:
                </p>
                <ul className="space-y-2 text-gray-300">
                  <li><strong>E-posta:</strong> <a href="mailto:privacy@savnot.com" className="text-emerald-400 hover:text-emerald-300">privacy@savnot.com</a></li>
                  <li><strong>Destek:</strong> <a href="mailto:support@savnot.com" className="text-emerald-400 hover:text-emerald-300">support@savnot.com</a></li>
                  <li><strong>Web:</strong> <a href="https://savnot.com" className="text-emerald-400 hover:text-emerald-300">https://savnot.com</a></li>
                </ul>
              </div>
            </section>

            {/* Footer */}
            <div className="mt-12 pt-6 border-t border-gray-700 text-center">
              <p className="text-gray-400 text-sm">
                © 2025 SavNot. Tüm hakları saklıdır.
              </p>
              <p className="text-gray-500 text-xs mt-2">
                KVKK ve GDPR uyumlu veri işleme politikası
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
